/**
 * Secondary CTA Decision Engine
 *
 * Pure, synchronous. `resolveSecondaryCtas` returns up to two CTAs rendered
 * underneath the primary CTA, given already-fetched supporter data + the
 * resolved primary CTAs.
 *
 * Priority for CTA 1:
 *   "No sponsorships" primary → active appeal replaces Donate; Volunteer remains
 *   Tax season → Tax receipts
 *   Active pledge → Beyond Measure
 *   Not a sponsor → active appeal replaces Donate; Volunteer remains
 *   Sponsor → sponsor sub-flow
 *
 * Sponsor sub-flow (fills remaining slots, in order):
 *   1. No payment method on file → Add payment method
 *   2. Write a letter (specific children if >60d stale, else generic)
 *   3. Active appeals — except Christmas Gift Catalog when Christmas is primary
 *   4. Send a Gift — unless the primary CTA is a monetary ask (birthday /
 *      Christmas gift), in which case default to Volunteer (no double money ask).
 *
 * Time-based branches read the current date internally — no injected clock.
 * Only the result types, input type, and resolver are exported; the decision
 * predicates and builders below are private to this module.
 */
import { differenceInDays } from 'date-fns';

import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import type {
  GraphQLCommitment,
  GraphQLPledge,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import {
  filterActiveSponsorshipCommitments,
  isSponsorshipCommitmentGiftable,
  isSponsorshipCommitmentWritable,
} from '@sola/shared-utils/supporters/commitments.ts';
import { isDuringTaxSeason } from '@sola/shared-utils/supporters/financials.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { isMonthDayWithinRange } from '@sola/utils/date.ts';
import { getElapsed } from '@sola/utils/relativeTime.ts';

import {
  christmasGiftCatalogAppealMetadata,
  giveThanksAppealMetadata,
} from '../../organisms/notifications/templates/appeal-metadata.ts';
import type { PrimaryCtaResult } from './primary-cta-decision.ts';
import { PrimaryCtaType } from './primary-cta-decision.ts';

// ─── Types ─────────────────────────────────────────────────────────────────

export enum SecondaryCtaType {
  TaxReceipts = 'tax-receipts',
  BeyondMeasure = 'beyond-measure',
  DonateToCause = 'donate-to-cause',
  Volunteer = 'volunteer',
  AddPaymentMethod = 'add-payment-method',
  WriteLetterSpecific = 'write-letter-specific',
  WriteLetterGeneric = 'write-letter-generic',
  SendGift = 'send-gift',
  GiveThanks = 'give-thanks',
  ChristmasGiftCatalog = 'christmas-gift-catalog',
}

// Rendered as a LinkCard, so it carries i18n keys + interpolation rather than
// resolved strings.
type SecondaryCtaResultBase = {
  type: SecondaryCtaType;
  titleKey: string;
  subtitleKey?: string;
  interpolation?: Record<string, string>;
  iconName: IconNames;
};
type SecondaryCtaResultUrl = {
  hrefType: 'url';
  url: string;
};
type SecondaryCtaResultHrefKey = {
  hrefKey: string;
};

export type SecondaryCtaResult = SecondaryCtaResultBase &
  (SecondaryCtaResultUrl | SecondaryCtaResultHrefKey);

/** The homepage renders up to two secondary CTAs underneath the primary CTA. */
export type SecondaryCtaResolution = SecondaryCtaResult[];

export type SecondaryCtaInput = {
  commitments: GraphQLCommitment[];
  pledges: GraphQLPledge[];
  paymentMethods: GraphQLSupporterPaymentMethod[];
  primaryCtas: PrimaryCtaResult[];
};

const LETTER_STALE_DAYS = 60;

/**
 * Interpolation sentinel for the write-letter CTA. When a sponsorship has no
 * correspondence date, the (pure) engine can't translate, so it emits this key
 * as the `lastWritten` value; the rendering component detects it and resolves
 * the translated fallback ("N/A").
 */
export const lastWrittenFallbackKey =
  'my:homepage.overview.writeCta.lastWritten_fallback';

// ─── Decision predicates ─────────────────────────────────────────────────────

/** Is it tax season? (drives the tax-receipts CTA) */
const isTaxSeason = (): boolean => isDuringTaxSeason();

/** Does the supporter have an active pledge? (drives Beyond Measure) */
const hasActivePledge = (pledges: GraphQLPledge[]): boolean =>
  pledges.some((p) => p.status === CommitmentStatusEnum.Active);

/** Does the supporter have any payment method on file? */
const hasAnyPaymentMethod = (
  paymentMethods: GraphQLSupporterPaymentMethod[],
): boolean => paymentMethods.length > 0;

/** Is the primary CTA the "No sponsorships" hard stop? */
const primaryIsNoSponsorships = (primaryCtas: PrimaryCtaResult[]): boolean =>
  primaryCtas.some((cta) => cta.type === PrimaryCtaType.NoSponsorships);

/** Is Christmas already represented by a primary CTA? */
const primaryHasChristmas = (primaryCtas: PrimaryCtaResult[]): boolean =>
  primaryCtas.some((cta) => cta.type === PrimaryCtaType.Christmas);

/** Is the primary CTA a monetary "ask" (birthday or Christmas gift)? */
const primaryHasMonetaryAsk = (primaryCtas: PrimaryCtaResult[]): boolean =>
  primaryCtas.some(
    (cta) =>
      cta.type === PrimaryCtaType.Christmas ||
      (cta.type === PrimaryCtaType.Birthday && !cta.confirmed),
  );

// ─── Letter helpers ──────────────────────────────────────────────────────────

const isLetterStale = (
  lastCorrespondenceDate: string | null | undefined,
  now: Date,
): boolean => {
  if (!lastCorrespondenceDate) {
    return true;
  }
  return (
    differenceInDays(now, new Date(lastCorrespondenceDate)) > LETTER_STALE_DAYS
  );
};

const getOldestCorrespondenceDate = (
  commitments: GraphQLCommitment[],
): string | null => {
  const dates = commitments
    .map((c) => c.lastCorrespondenceDate)
    .filter((d): d is string => d != null);

  return dates.length === 0 ? null : dates.toSorted()[0];
};

// ─── CTA builders ────────────────────────────────────────────────────────────

const buildTaxReceiptsCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.TaxReceipts,
  hrefKey: 'my:homepage.overview.taxReceiptsCta.url',
  titleKey: 'my:homepage.overview.taxReceiptsCta.title',
  subtitleKey: 'my:homepage.overview.taxReceiptsCta.subtitle',
  iconName: 'receipt-check',
});

const buildBeyondMeasureCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.BeyondMeasure,
  hrefKey: 'my:homepage.overview.pledgeCta.url',
  titleKey: 'my:homepage.overview.pledgeCta.title',
  subtitleKey: 'my:homepage.overview.pledgeCta.subtitle',
  iconName: 'skippy-filled',
});

const buildDonateToCauseCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.DonateToCause,
  hrefKey: 'my:homepage.overview.donateCta.url',
  titleKey: 'my:homepage.overview.donateCta.title',
  subtitleKey: 'my:homepage.overview.donateCta.subtitle',
  iconName: 'heart-hand',
});

const buildVolunteerCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.Volunteer,
  hrefType: 'url',
  url: `${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM || 'https://www.compassion.com'}${knownSkippyUrls.volunteers}`,
  titleKey: 'my:homepage.overview.volunteerCta.title',
  subtitleKey: 'my:homepage.overview.volunteerCta.subtitle',
  iconName: 'volunteer',
});

const buildAddPaymentMethodCta = (): SecondaryCtaResult => ({
  type: SecondaryCtaType.AddPaymentMethod,
  hrefKey: 'my:homepage.overview.paymentMethodCta.url',
  titleKey: 'my:homepage.overview.paymentMethodCta.title',
  subtitleKey: 'my:homepage.overview.paymentMethodCta.subtitle',
  iconName: 'wallet-02',
});

const buildSendGiftCta = (childCount: number): SecondaryCtaResult => ({
  type: SecondaryCtaType.SendGift,
  hrefKey: 'my:homepage.overview.giftCta.url',
  titleKey: 'my:homepage.overview.giftCta.title',
  subtitleKey:
    childCount > 1
      ? 'my:homepage.overview.giftCta.subtitleMulti'
      : 'my:homepage.overview.giftCta.subtitle',
  iconName: 'family-gift',
});

const appealDefinitions = [
  {
    type: SecondaryCtaType.GiveThanks,
    metadata: giveThanksAppealMetadata,
  },
  {
    type: SecondaryCtaType.ChristmasGiftCatalog,
    metadata: christmasGiftCatalogAppealMetadata,
  },
] as const;

const buildActiveAppealCtas = (
  now: Date,
  primaryCtas: PrimaryCtaResult[],
): SecondaryCtaResolution =>
  appealDefinitions
    .filter(
      ({ type, metadata }) =>
        isMonthDayWithinRange(
          metadata.startMonthDay,
          metadata.endMonthDay,
          now,
        ) &&
        !(
          type === SecondaryCtaType.ChristmasGiftCatalog &&
          primaryHasChristmas(primaryCtas)
        ),
    )
    .map(
      ({ type, metadata }): SecondaryCtaResult => ({
        type,
        hrefType: 'url',
        url: metadata.href,
        titleKey: metadata.titleTranslationKey,
        subtitleKey: metadata.descriptionTranslationKey,
        iconName: metadata.iconName,
      }),
    );

/** An active appeal replaces Donate; Volunteer always keeps the second slot. */
const resolveNonSponsorSecondaryCtas = (
  appealCtas: SecondaryCtaResolution,
): SecondaryCtaResolution => [
  appealCtas[0] ?? buildDonateToCauseCta(),
  buildVolunteerCta(),
];

/**
 * Builds the write-letter CTA. When any writable sponsorship has a stale
 * (>60 day) letter, targets those specific children; otherwise falls back to
 * the generic write-a-letter prompt.
 */
const buildWriteLetterCta = (
  sponsorships: GraphQLCommitment[],
): SecondaryCtaResult => {
  const now = new Date();
  const writable = sponsorships.filter(isSponsorshipCommitmentWritable);
  const stale = writable.filter((c) =>
    isLetterStale(c.lastCorrespondenceDate, now),
  );

  const isSpecific = stale.length > 0;
  const source = isSpecific ? stale : writable;
  const oldestDate = getOldestCorrespondenceDate(source);
  // No date → emit the fallback key; the component translates it.
  const lastWritten = oldestDate
    ? getElapsed(oldestDate) || ''
    : lastWrittenFallbackKey;

  let titleKey: string;
  let interpolation: Record<string, string>;

  if (source.length === 1) {
    titleKey = 'my:homepage.overview.writeCta.title';
    interpolation = {
      childName: source[0].participant?.name ?? '',
      lastWritten,
    };
  } else if (source.length === 2) {
    titleKey = 'my:homepage.overview.writeCta.title_two';
    interpolation = {
      name1: source[0].participant?.name ?? '',
      name2: source[1].participant?.name ?? '',
      lastWritten,
    };
  } else {
    titleKey = 'my:homepage.overview.writeCta.title_other';
    interpolation = { lastWritten };
  }

  return {
    type: isSpecific
      ? SecondaryCtaType.WriteLetterSpecific
      : SecondaryCtaType.WriteLetterGeneric,
    hrefKey: 'my:homepage.overview.writeCta.url',
    titleKey,
    subtitleKey: 'my:homepage.overview.writeCta.subtitle',
    interpolation,
    iconName: 'edit-04',
  };
};

/** Count of active child sponsorships (used for gift CTA copy). */
const getGiftableSponsorshipCount = (
  sponsorships: GraphQLCommitment[],
): number =>
  sponsorships.filter((c) => isSponsorshipCommitmentGiftable(c)).length;

// ─── Resolver ────────────────────────────────────────────────────────────────

/** Sponsor sub-flow — fills up to `limit` slots in priority order. */
const resolveSponsorSecondaryCtas = (
  paymentMethods: GraphQLSupporterPaymentMethod[],
  primaryCtas: PrimaryCtaResult[],
  appealCtas: SecondaryCtaResolution,
  limit: number,
  sponsorships: GraphQLCommitment[],
): SecondaryCtaResolution => {
  const ctas: SecondaryCtaResolution = [];

  // TODO(CTA): confirm "Add payment method" should outrank the letter CTA
  // under the monetary-ask "default to Write / Volunteer" rule.
  if (!hasAnyPaymentMethod(paymentMethods)) {
    ctas.push(buildAddPaymentMethodCta());
  }

  ctas.push(buildWriteLetterCta(sponsorships), ...appealCtas);

  // A monetary-ask primary suppresses the default "ask" secondary — default
  // to Volunteer instead of Send a Gift. Campaign appeals retain their priority
  // after an equivalent Christmas appeal has been filtered out above.
  if (primaryHasMonetaryAsk(primaryCtas)) {
    ctas.push(buildVolunteerCta());
  } else {
    ctas.push(buildSendGiftCta(getGiftableSponsorshipCount(sponsorships)));
  }

  return ctas.slice(0, limit);
};

/**
 * Resolves the secondary CTA(s) for the home page. Pure — reads the current
 * date via the decision functions; no injected clock. i18n keys (including the
 * "last written" fallback) are resolved by the rendering component.
 */
export const resolveSecondaryCtas = ({
  commitments,
  paymentMethods,
  pledges,
  primaryCtas,
}: SecondaryCtaInput): SecondaryCtaResolution => {
  // Apply CTA logic against active sponsorships - exclude sponsorships in transition
  const remainingSponsorships = filterActiveSponsorshipCommitments(commitments);
  const appealCtas = buildActiveAppealCtas(new Date(), primaryCtas);

  // "Sponsor a child" remains a hard stop over tax/pledge CTAs, while an active
  // campaign appeal replaces Donate in the non-sponsor pair.
  if (primaryIsNoSponsorships(primaryCtas)) {
    return resolveNonSponsorSecondaryCtas(appealCtas);
  }

  // Tax season → CTA 1 = Tax receipts (+ sponsor sub-flow for CTA 2)
  // TODO(CTA): confirm whether tax-season non-sponsors should get a 2nd CTA.
  if (isTaxSeason()) {
    const ctas: SecondaryCtaResolution = [buildTaxReceiptsCta()];
    if (remainingSponsorships.length) {
      ctas.push(
        ...resolveSponsorSecondaryCtas(
          paymentMethods,
          primaryCtas,
          appealCtas,
          1,
          remainingSponsorships,
        ),
      );
    }
    return ctas.slice(0, 2);
  }

  // Active pledge → CTA 1 = Beyond Measure (+ sponsor sub-flow for CTA 2)
  if (hasActivePledge(pledges)) {
    const ctas: SecondaryCtaResolution = [buildBeyondMeasureCta()];
    if (remainingSponsorships.length) {
      ctas.push(
        ...resolveSponsorSecondaryCtas(
          paymentMethods,
          primaryCtas,
          appealCtas,
          1,
          remainingSponsorships,
        ),
      );
    }
    return ctas.slice(0, 2);
  }

  // Not a sponsor → an active appeal replaces Donate; Volunteer remains.
  if (remainingSponsorships.length === 0) {
    return resolveNonSponsorSecondaryCtas(appealCtas);
  }

  // Sponsor → run the sponsor sub-flow for both slots
  return resolveSponsorSecondaryCtas(
    paymentMethods,
    primaryCtas,
    appealCtas,
    2,
    remainingSponsorships,
  );
};
